import React, { useState, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { Modal } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import CloseIcon from "@material-ui/icons/Close";
import { TIFFViewer } from "react-tiff";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";

const fadeIn = keyframes`from{opacity:0;transform:scale(.97)}to{opacity:1;transform:scale(1)}`;
const spin = keyframes`to{transform:rotate(360deg)}`;
const bar = keyframes`0%{background-position:-400px 0}100%{background-position:400px 0}`;

const Skeleton = ({ label }) => (
  <SkeletonWrap>
    <Spinner />
    <SkBars>
      {[82, 60, 95, 50, 74, 40, 88].map((w, i) => (
        <SkBar
          key={i}
          style={{ width: `${w}%`, animationDelay: `${i * 0.12}s` }}
        />
      ))}
    </SkBars>
    <SkLabel>{label || "Loading…"}</SkLabel>
  </SkeletonWrap>
);

const IframeViewer = ({ src, fallbackSrc, label }) => {
  const [loading, setLoading] = useState(true);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [triedFallback, setTriedFallback] = useState(false);
  const timerRef = useRef(null);

  const startTimer = (nextSrc) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (fallbackSrc && !triedFallback) {
        setTriedFallback(true);
        setCurrentSrc(fallbackSrc);
        setLoading(true);
        startTimer(fallbackSrc);
      } else {
        setLoading(false);
      }
    }, 12000);
  };

  useEffect(() => {
    startTimer(currentSrc);
    return () => clearTimeout(timerRef.current); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSrc]);

  const onLoad = () => {
    clearTimeout(timerRef.current);
    setLoading(false);
  };

  return (
    <IframeWrap>
      {loading && (
        <SkeletonAbsolute>
          <Skeleton label={label} />
        </SkeletonAbsolute>
      )}
      <TheIframe
        key={currentSrc}
        src={currentSrc}
        title="preview"
        onLoad={onLoad}
        onError={() => {
          clearTimeout(timerRef.current);
          if (fallbackSrc && !triedFallback) {
            setTriedFallback(true);
            setCurrentSrc(fallbackSrc);
            setLoading(true);
          } else {
            setLoading(false);
          }
        }}
        scrolling="yes"
        allowFullScreen
        frameBorder="0"
      />
    </IframeWrap>
  );
};

const DocViewers = ({ docview, setDocView }) => {
  if (!docview?.state || !docview?.url) return null;

  const close = () => setDocView({ state: false });
  const fileName = docview.name || "Document";
  const raw = docview.url.split("?")[0];
  const ext = (
    docview.format?.toLowerCase() ||
    raw.split(".").pop()?.toLowerCase() ||
    ""
  ).trim();

  const encoded = encodeURIComponent(docview.url);
  const googleViewer = `https://docs.google.com/gview?url=${encoded}&embedded=true`;
  const officeViewer = `https://view.officeapps.live.com/op/embed.aspx?src=${encoded}`;

  const isDocx = ["docx", "doc", "pptx", "ppt"].includes(ext);
  const isPdf = ext === "pdf";
  const isTiff = ["tif", "tiff"].includes(ext);
  const isSheet = ["xlsx", "xls", "csv"].includes(ext);
  const isImage = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(
    ext,
  );
  const isText = ["txt", "xml", "json", "html", "htm", "md"].includes(ext);

  const safeUrl =
    docview.url + (docview.url.includes("?") ? "&" : "?") + `_t=${Date.now()}`;
  const docs = [{ uri: safeUrl, fileName, fileType: ext }];

  return (
    <Modal open={docview.state} onClose={close} disableEnforceFocus>
      <Overlay>
        <ModalCard>
          {/* Header */}
          <TopBar>
            <LeftSide>
              <ExtPill ext={ext}>{ext.toUpperCase() || "FILE"}</ExtPill>
              <DocName title={fileName}>{fileName}</DocName>
            </LeftSide>
            <RightSide>
              <DownBtn
                href={docview.url}
                download={fileName}
                target="_blank"
                rel="noopener noreferrer"
              >
                <GetAppIcon style={{ fontSize: 16 }} /> Download
              </DownBtn>
              <CloseBtn onClick={close}>
                <CloseIcon style={{ fontSize: 17 }} />
              </CloseBtn>
            </RightSide>
          </TopBar>

          <ViewArea>
            {isDocx && (
              <IframeViewer
                src={officeViewer}
                fallbackSrc={googleViewer}
                label="Loading document…"
              />
            )}

            {isPdf && (
              <IframeViewer
                src={docview.url}
                fallbackSrc={googleViewer}
                label="Loading PDF…"
              />
            )}

            {isSheet && (
              <IframeViewer
                src={officeViewer}
                fallbackSrc={googleViewer}
                label="Loading spreadsheet…"
              />
            )}

            {isTiff && (
              <TiffArea>
                <TIFFViewer
                  tiff={safeUrl}
                  paginate="bottom"
                  zoomable
                  printable={false}
                />
              </TiffArea>
            )}

            {isImage && (
              <ImageArea>
                <img
                  src={docview.url}
                  alt={fileName}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: 8,
                  }}
                />
              </ImageArea>
            )}

            {isText && <IframeViewer src={docview.url} label="Loading file…" />}

            {!isDocx &&
              !isPdf &&
              !isSheet &&
              !isTiff &&
              !isImage &&
              !isText && (
                <DocViewerWrap>
                  <DocViewer
                    documents={docs}
                    pluginRenderers={DocViewerRenderers}
                    config={{
                      header: { disableHeader: true },
                      loadingRenderer: {
                        overrideComponent: () => <Skeleton label="Loading…" />,
                      },
                    }}
                    style={{ width: "100%", height: "100%" }}
                  />
                </DocViewerWrap>
              )}
          </ViewArea>
        </ModalCard>
      </Overlay>
    </Modal>
  );
};

export default DocViewers;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  outline: none;
`;

const ModalCard = styled.div`
  width: 96vw;
  max-width: 1280px;
  height: 92vh; /* ← fixed viewport height */
  display: flex;
  flex-direction: column; /* ← column layout */
  background: #f4f5f7;
  border-radius: 18px;
  overflow: hidden; /* ← clips children to rounded corners only */
  box-shadow:
    0 40px 120px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(255, 255, 255, 0.06);
  animation: ${fadeIn} 0.2s ease;
  outline: none;
`;

const TopBar = styled.div`
  flex: 0 0 auto;
  background: linear-gradient(135deg, #00838c 0%, #00606a 100%);
  padding: 11px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.25);
  z-index: 2;
`;

const LeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;
const RightSide = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const extColor = {
  pdf: "#e74c3c",
  docx: "#2471a3",
  doc: "#2471a3",
  xlsx: "#1e8449",
  xls: "#1e8449",
  csv: "#148f77",
  tif: "#7d3c98",
  tiff: "#7d3c98",
  pptx: "#d35400",
  ppt: "#d35400",
  txt: "#5d6d7e",
  json: "#b7950b",
  xml: "#1a5276",
};

const ExtPill = styled.span`
  background: ${(p) => extColor[p.ext] || "rgba(255,255,255,0.2)"};
  color: #fff;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: 1px;
  padding: 4px 10px;
  border-radius: 6px;
  flex-shrink: 0;
  text-transform: uppercase;
`;

const DocName = styled.span`
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DownBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #fff;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.15);
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  padding: 6px 14px;
  border-radius: 8px;
  transition: background 0.18s;
  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }
`;

const CloseBtn = styled.button`
  background: rgba(255, 255, 255, 0.13);
  border: 1.5px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  cursor: pointer;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s;
  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }
`;

const ViewArea = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const IframeWrap = styled.div`
  position: relative;
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
  height: 100%; /* fallback for non-flex contexts */
  background: #fff;
`;

const TheIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  background: #fff;
  display: block;
`;

const TiffArea = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px;
  background: #fff;
`;

const ImageArea = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #fff;
`;

const DocViewerWrap = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const SkeletonAbsolute = styled.div`
  position: absolute;
  inset: 0;
  background: #fff;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SkeletonWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
`;

const Spinner = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 3px solid #e5e7eb;
  border-top-color: #00838c;
  animation: ${spin} 0.7s linear infinite;
`;

const SkBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  width: 240px;
`;

const SkBar = styled.div`
  height: 9px;
  border-radius: 5px;
  background: linear-gradient(90deg, #efefef 25%, #e0e0e0 50%, #efefef 75%);
  background-size: 400px 100%;
  animation: ${bar} 1.4s ease infinite;
`;

const SkLabel = styled.p`
  color: #9ca3af;
  font-size: 13px;
  margin: 0;
  font-weight: 500;
`;
